import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Sparkles,
  Lightbulb,
  ChevronRight,
} from 'lucide-react';
import { useCatalogsStore } from '@/stores/catalogs';
import type { OntologicalSchemaEntry } from '@/services/api';

const UOS_CLASS_OPTIONS_FALLBACK = [
  'terminal_value',
  'instrumental_value',
  'functional_need',
  'emotional_need',
  'social_need',
  'purchase_scenario',
  'life_stage_context',
  'market_concept',
  'brand_attribute',
  'product_attribute',
  'consumer_archetype',
  'self_concept_signal',
  'friction_factor',
  'decision_trigger',
];

function formatLabel(key: string): string {
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

interface ProposedExtensionsListProps {
  schema: Record<string, OntologicalSchemaEntry>;
}

export function ProposedExtensionsList({ schema }: ProposedExtensionsListProps) {
  const {
    uosExtensions,
    uosLoading,
    actingId,
    fetchUosExtensions,
    actOnUosExtension,
  } = useCatalogsStore();

  const [expanded, setExpanded] = useState<string | null>(null);
  const [reclassTarget, setReclassTarget] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchUosExtensions();
  }, [fetchUosExtensions]);

  const classOptions = Object.keys(schema).filter((k) => k !== 'proposed_extension');
  const options = classOptions.length > 0 ? classOptions : UOS_CLASS_OPTIONS_FALLBACK;

  if (uosLoading && uosExtensions.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-400 py-8 justify-center">
        <Loader2 size={14} className="animate-spin" /> Loading proposed extensions...
      </div>
    );
  }

  if (uosExtensions.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <Sparkles size={24} className="text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500">
          No proposed UOS extensions pending review.
        </p>
        <p className="text-xs text-slate-400 mt-1">
          The OntologicalReasoningAgent surfaces candidates here when it encounters
          entities that don't fit any existing class.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {uosExtensions.map((ext) => {
        const isExpanded = expanded === ext.id;
        const busy = actingId === `uos:${ext.id}`;
        const target = reclassTarget[ext.id] ?? '';

        return (
          <div
            key={ext.id}
            className="bg-white rounded-xl border border-slate-200"
          >
            <button
              onClick={() => setExpanded(isExpanded ? null : ext.id)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors rounded-xl"
            >
              <Lightbulb size={16} className="text-amber-500 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-navy-900 truncate">
                    {ext.proposed_class_name}
                  </span>
                  <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                    proposed
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                  <span>from</span>
                  <Link
                    to={`/knowledge/estate/entities/${ext.source_canonical_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-brand-teal hover:text-brand-teal-dark truncate"
                  >
                    {ext.canonical_name}
                  </Link>
                  <span className="text-slate-400">({ext.entity_type})</span>
                </div>
              </div>
              <span className="text-xs text-emerald-600 font-medium flex-shrink-0">
                {(ext.confidence * 100).toFixed(0)}%
              </span>
              <ChevronRight
                size={14}
                className={clsx(
                  'text-slate-400 transition-transform flex-shrink-0',
                  isExpanded && 'rotate-90',
                )}
              />
            </button>

            {isExpanded && (
              <div className="border-t border-slate-100 px-4 py-3 space-y-3 bg-slate-50/50">
                {ext.proposed_class_rationale && (
                  <div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                      Rationale
                    </div>
                    <p className="text-xs text-slate-700">{ext.proposed_class_rationale}</p>
                  </div>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() =>
                      actOnUosExtension(ext.id, 'approved', { reviewed_by: 'admin-ui' })
                    }
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-40"
                  >
                    {busy ? (
                      <Loader2 size={11} className="animate-spin" />
                    ) : (
                      <CheckCircle2 size={11} />
                    )}
                    Approve
                  </button>
                  <span className="text-xs text-slate-400">or reject & reclassify into</span>
                  <select
                    value={target}
                    onChange={(e) =>
                      setReclassTarget((prev) => ({ ...prev, [ext.id]: e.target.value }))
                    }
                    className="text-xs bg-white border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-teal"
                  >
                    <option value="">Select UOS class…</option>
                    {options.map((k) => (
                      <option key={k} value={k}>
                        {formatLabel(k)}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() =>
                      target &&
                      actOnUosExtension(ext.id, 'rejected', {
                        reclassify_to: target,
                        reviewed_by: 'admin-ui',
                      })
                    }
                    disabled={busy || !target}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-40"
                  >
                    <XCircle size={11} />
                    Reject
                  </button>
                </div>

                <p className="text-[10px] text-slate-500 italic">
                  Approving marks the extension for schema promotion in Phase 2
                  (requires a code-level UOS enum update); the source canonical
                  stays tagged as <code>proposed_extension</code> until promotion.
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
